import hsFetch, { getCSRFToken } from 'hellospa/hs-fetch';
import * as Yup from 'yup';
import { appendQueryParameters, queryParams } from 'hellospa/common/utils/fetch';
import {
  FileUploadResponse,
  FilesDeleteResponse,
  FilePollResponse,
  FileReorderResponse,
  FilePollResponseStatus,
  ExternalFileDownloadResponse,
  ExternalFolderResponse,
  EmbeddedPollResponse,
  embeddedDataSchema,
  ExternalServiceAuthStatusResponse,
  UserFile,
  UserFileTypes,
  UserFilesKeyed,
  Contact,
  RequestTypes,
  fileUploadResponseSchema,
  recipientSchema,
  CoverPage,
  Recipient,
  RecipientTypes,
  UpdateAccountResponse,
  FileSetPasswordResponse,
  flagsSchema,
  settingsSchema,
  TemplateCreateStatus,
  requestTypesSchema,
  apiAppSchema,
  ExampleFields,
  BulkSendInitResponse,
  bulkSendDataSchema,
  ccSchema,
  Document,
  CC,
  EditorFields,
} from 'hellospa/page/prep-and-send/data/types';
import { Rule } from 'hellospa/conditional-logic/types';
import { lazyUnion, InferUnion } from 'hellospa/common/utils/yup';
import { notEmpty, unreachable } from 'hellospa/ts-utils';
import { Page, FieldTypes } from 'hellospa/components/editor/types';
import { fontFamilies, signatureColors } from 'hellospa/components/editor/constants';

export type ImportContactsStatusResponse = {
  success: boolean,
  complete: boolean,
  progress: number,
  count: number,
}

export type TemplateResponse = {
  guid: string,

  requestType: RequestTypes,
  recipientReassignment: boolean,
  recipientOrder: boolean,

  dateFormat: string,
  jsDateFormat: string,

  templateGuid: string,

  files: FileUploadResponse[],
  editorFields: EditorFields
  recipients: Recipient[],
  ccs: CC[],
  document: Document,
};

const pageSchema = Yup.object<Page>({
  src: Yup.string().required(),
  width: Yup.number().required(),
  height: Yup.number().required(),
  documentId: Yup.string(),
  orientation: Yup.mixed().oneOf([0, 1]),
});

const ruleSchema = Yup.object<Rule>({
  id: Yup.string().required(),
  triggerOperator: Yup.mixed().oneOf(['AND', 'OR']),
  triggers: Yup.array(),
  actions: Yup.array(),
});

const phpBaseField = {
  id: Yup.string().required(),
  invalidApiId: Yup.string().notRequired(),
  pageIndex: Yup.number().required(),
  name: Yup.string().notRequired(),
  x: Yup.number().required(),
  y: Yup.number().required(),
  required: Yup.bool().notRequired(),
  originalRequired: Yup.bool().notRequired(),
  signer: Yup.string().required(),
  width: Yup.number().required(),
  height: Yup.number().required(),
  documentId: Yup.string().required(),
  hidden: Yup.boolean().notRequired(),
  readOnly: Yup.boolean().notRequired(),
  linkId: Yup.string().nullable().notRequired(),
};

const phpFieldStatus = {
  [FieldTypes.Checkbox]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Checkbox>(),
    checked: Yup.boolean().notRequired().nullable(),
    group: Yup.string().nullable().notRequired(),
    requirement: Yup.string().nullable().notRequired(),
    groupLabel: Yup.string().nullable().notRequired(),
    isEditableMergeField: Yup.boolean().notRequired(),
  }),
  [FieldTypes.Date]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Date>(),
    placeholder: Yup.string().notRequired(),
    dateFormat: Yup.string().notRequired(),
    fontFamily: Yup.mixed().oneOf([...fontFamilies]),
    fontSize: Yup.number().notRequired(),
    value: Yup.string().notRequired().nullable(),
  }),
  [FieldTypes.Initials]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Initials>(),
    signatureColors: Yup.mixed().oneOf([...signatureColors]),
    signature: Yup.string().notRequired(),
  }),
  [FieldTypes.Signature]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Signature>(),
    signatureColors: Yup.mixed().oneOf([...signatureColors]),
    signature: Yup.string().notRequired(),
  }),
  [FieldTypes.Text]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Text>(),
    placeholder: Yup.string().notRequired(),
    lineHeight: Yup.number().notRequired(),
    fontFamily: Yup.mixed().oneOf([...fontFamilies]),
    fontSize: Yup.number().notRequired(),
    value: Yup.string().notRequired().nullable(),
    masked: Yup.bool().notRequired(),
    isEditableMergeField: Yup.boolean().notRequired(),
    originalFontSize: Yup.number().notRequired(),
    lines: Yup.string().notRequired(),
  }),
  [FieldTypes.Dropdown]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Dropdown>(),
    options: Yup.array().of(Yup.string()),
    value: Yup.string().notRequired().nullable(),
    fontFamily: Yup.mixed().oneOf([...fontFamilies]),
    fontSize: Yup.number().notRequired(),
  }),
  [FieldTypes.Radio]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Radio>(),
    checked: Yup.boolean().required(),
    group: Yup.string().required(),
    requirement: Yup.string().nullable().required(),
    groupLabel: Yup.string().nullable().notRequired(),
  }),
  [FieldTypes.Hyperlink]: Yup.object({
    ...phpBaseField,
    type: Yup.mixed<FieldTypes.Hyperlink>(),
    lineHeight: Yup.number().notRequired(),
    fontFamily: Yup.mixed().oneOf([...fontFamilies]),
    fontSize: Yup.number().notRequired(),
    value: Yup.string().required().nullable(),
    url: Yup.string().required(),
  }),
};

const fileSchema = lazyUnion<InferUnion<typeof phpFieldStatus>, 'type'>('type', phpFieldStatus);


export const templateResponse = Yup.object<TemplateResponse>({
  guid: Yup.string().required(),

  requestType: requestTypesSchema,
  recipientReassignment: Yup.bool().required(),
  recipientOrder: Yup.bool().required(),

  dateFormat: Yup.string().required(),
  jsDateFormat: Yup.string().required(),

  templateGuid: Yup.string().required(),
  files: Yup.array().of(fileUploadResponseSchema),
  editorFields: Yup.object({
    pages: Yup.array().of(pageSchema),
    fields: Yup.array().of(fileSchema),
    rules: Yup.array().of(ruleSchema),
  }),
  recipients: Yup.array().of(recipientSchema),
  ccs: Yup.array().of(ccSchema),
  document: Yup.object({
    title: Yup.string().ensure(),
    message: Yup.string().ensure(),
  }).required(),
});

export const dataSchema = Yup.object({
  // The save endpoint sends this data object, so if you have data the user
  // can't change, it doesn't belong here.
  data: Yup.object({
    guid: Yup.string().required(),
    settings: settingsSchema,
    files: Yup.array().of(fileUploadResponseSchema),
    editorFields: Yup.object({
      pages: Yup.array().of(pageSchema),
      fields: Yup.array().of(fileSchema),
      rules: Yup.array().of(ruleSchema),
    }),
    recipients: Yup.array().of(recipientSchema).ensure(),
    ccs: Yup.array().of(ccSchema).ensure(),
    document: Yup.object({
      title: Yup.string().ensure(),
      message: Yup.string().ensure(),
    }).required(),
    templates: Yup.array().of(templateResponse),

    coverPage: Yup.object<CoverPage>({
      from: Yup.string().ensure(),
      to: Yup.string().ensure(),
      message: Yup.string().ensure(),
    }),
    bulkSend: bulkSendDataSchema,
  }),

  flags: flagsSchema,

  user: Yup.object({
    name: Yup.string().ensure(),
    email: Yup.string().email().required(),
    isPaidAccount: Yup.boolean(),
    accountDateFormat: Yup.string(),
  }).required(),

  apiApp: apiAppSchema,
  embeddedData: embeddedDataSchema,
});
export type DataSchema = Yup.InferType<typeof dataSchema>

export type TemplateErrorResponse = {
  error: string,
};
export const templateErrorResponse = Yup.object<TemplateErrorResponse>({
  error: Yup.string().required(),
});

const sendRequestErrorSchema = Yup.object({
  message: Yup.string(),
});

const sendRequestUiResponseSchema = Yup.object({
  redirectUrl: Yup.string().notRequired().trim(),
  templateGuid: Yup.string().notRequired().trim(),
  status: Yup.string().notRequired().trim(),
  errorMsg: Yup.string().notRequired().trim(),
  code: Yup.string().notRequired().trim(),
  error: Yup.string().notRequired().trim(),
  errors: Yup.array().of(sendRequestErrorSchema).notRequired(),
  success: Yup.bool().notRequired(),

  needsCharge: Yup.bool().notRequired(),
  chargeType: Yup.string().notRequired(),
  pricePerFaxInCents: Yup.number().notRequired(),
  totalPriceInCents: Yup.number().notRequired(),
  freeFaxPagesLeft: Yup.number().notRequired(),
  faxPageCurrentCount: Yup.number().notRequired(),
  overageFaxMaxLimitInCents: Yup.number().notRequired(),
  initialPageLimit: Yup.number().notRequired(),
  overagePriceInCentsPerPage: Yup.number().notRequired(),
  internationalMultiplier: Yup.number().notRequired(),

  isFirstFax: Yup.bool().notRequired(),
  isAtFaxLimit: Yup.bool().notRequired(),
  templateConversionCandidateId: Yup.string().notRequired(),
  shouldTemplateCreateShow: Yup.boolean().notRequired(),
});

const sendEmbeddedRequestResponseSchema = Yup.object({
  success: Yup.bool().required(),
  signature_request_id: Yup.string().required(),
  signature_request_info: Yup.object({
    title: Yup.string(),
    message: Yup.string(),
    signatures: Yup.array().of(
      Yup.object({
        signer_name: Yup.string(),
        signer_email_address: Yup.string(),
        order: Yup.number().nullable(),
      }),
    ),
    cc_email_addresses: Yup.array().of(Yup.string()),
  }),
});

const sendEmbeddedTemplateResponseSchema = Yup.object({
  success: Yup.bool().required(),
  template_id: Yup.string().required(),
  template_status_id: Yup.string().required(),
  template_info: Yup.object({
    title: Yup.string(),
    message: Yup.string(),
    signer_roles: Yup.array().of(
      Yup.object({
        name: Yup.string(),
        order: Yup.number().nullable(),
      }),
    ),
    cc_roles: Yup.array().of(Yup.string()),
  }),
});

export type SendRequestUiResponse = Yup.InferType<typeof sendRequestUiResponseSchema>;
export type SendRequestEmbeddedRequestResponse = Yup.InferType<
  typeof sendEmbeddedRequestResponseSchema
>;
export type SendRequestEmbeddedTemplateResponse = Yup.InferType<
  typeof sendEmbeddedTemplateResponseSchema
>;

export type SendRequestResponse =
  | SendRequestUiResponse
  | SendRequestEmbeddedRequestResponse
  | SendRequestEmbeddedTemplateResponse;

const selfSaveRequestErrorSchema = Yup.object({
  message: Yup.string(),
});

const selfSaveRequestResponseSchema = Yup.object({
  redirectUrl: Yup.string().notRequired().trim(),
  errors: Yup.array().of(selfSaveRequestErrorSchema).notRequired(),
  success: Yup.bool().notRequired(),
  code: Yup.string().notRequired().trim(),
  error: Yup.string().notRequired().trim(),
  needsCharge: Yup.bool().notRequired(),
});

export type SelfSaveRequestResponse = Yup.InferType<typeof selfSaveRequestResponseSchema>;

export type SaveDataType = DataSchema['data'] & {
  requestType: RequestTypes,
};
export default function prepAndSendActions(csrfToken: string) {

  const getPrepAndSendData = async (transmissionGroupGuid: string): Promise<DataSchema> => {
    const response = await hsFetch(`/prep-and-send/data/${transmissionGroupGuid}`);
    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  };

  const savePrepAndSendData = async (data: SaveDataType): Promise<void> => {
    const response = await hsFetch(`/prep-and-send/save/${data.guid}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.status !== 200) {
      throw new Error('An error occured while saving P&S');
    }
  };

  const sendPrepAndSendRequest = async (data: SaveDataType, isFromChargeModal: boolean = false):
     Promise<SendRequestResponse> => {
    const agreedToChargeParam = isFromChargeModal ? `?agreed_to_charge=${isFromChargeModal}` : '';
    const response = await hsFetch(`/prep-and-send/send/${data.guid}${agreedToChargeParam}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const responseData = await response.json();
    let responseParsed;
    if ('needs_charge' in responseData) {
      responseParsed = await sendRequestUiResponseSchema.validate(responseData);
    } else if ('template_status_id' in responseData) {
      responseParsed = await sendEmbeddedTemplateResponseSchema.validate(responseData);
    } else if ('signature_request_id' in responseData) {
      responseParsed = await sendEmbeddedRequestResponseSchema.validate(responseData);
    } else {
      responseParsed = await sendRequestUiResponseSchema.validate(responseData);
      if (response.status === 200 && (responseParsed.redirectUrl || responseParsed.templateGuid)) {
        responseParsed.success = Boolean(responseParsed.success);
      } else {
        responseParsed.success = false;
      }
    }
    if (response.status !== 200 && (
      'errors' in responseParsed && (
        !responseParsed.errors
        || responseParsed.errors.length === 0
      )
    )) {
      throw new Error('Unhandled response from P&S send');
    }

    return responseParsed;
  };

  const selfSavePrepAndSendRequest = async (
    data: SaveDataType,
  ): Promise<SelfSaveRequestResponse> => {
    const response = await hsFetch(`/prep-and-send/self-save/${data.guid}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const responseParsed = await selfSaveRequestResponseSchema.validate(await response.json());
    if (response.status === 200 && responseParsed.redirectUrl) {
      responseParsed.success = true;
    } else {
      responseParsed.success = false;
      if (response.status !== 200 && (
        !responseParsed.errors
          || responseParsed.errors.length === 0
      )) {
        throw new Error('Unhandled response from P&S self-save');
      }
    }
    return responseParsed;
  };

  type Prepare = {
    hasSignerOrder: boolean,
    files: UserFile[],
    templateGuid?: string,
    requestType: RequestTypes,
    preloadedTsmGroupKey: string,
    templateOneOffFile: boolean,
    recipients: Recipient[],
    hiddenStepper: boolean,
    parentUrl?: string,
    editedTemplateGuid?: null | string,
    locale?: null | string,
  }
  const prepareEditor = async (prepare: Prepare): Promise<string> => {
    const formData = new FormData();
    formData.append('csrf_token', csrfToken);

    prepare.files.map((f) => f.guid).filter(notEmpty).forEach((guid) => {
      formData.append('snapshot_guids[]', guid);
    });
    formData.append('form_type', String(prepare.requestType));
    formData.append('is_reusable_link', '0');
    formData.append('preloaded_tsm_group_key', prepare.preloadedTsmGroupKey);
    formData.append('template_one_off_file', String(prepare.templateOneOffFile));
    formData.append('hidden_stepper', String(prepare.hiddenStepper));

    if (prepare.templateGuid) {
      formData.append('template_guid', prepare.templateGuid);
    }
    if (prepare.parentUrl) {
      formData.append('parent_url', prepare.parentUrl);
    }
    if (prepare.editedTemplateGuid) {
      formData.append('edited_template_guid', prepare.editedTemplateGuid);
    }
    if (prepare.locale) {
      formData.append('user_culture', prepare.locale);
    }

    const recipients = [...prepare.recipients];

    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];

      switch (recipient.type) {
        case RecipientTypes.Signer:
          formData.append(`recipients[${recipient.id}]`, recipient.email);
          formData.append(`recipient_names[${recipient.id}]`, recipient.name);
          if (prepare.hasSignerOrder) {
            formData.append(`signer_order[${recipient.id}]`, String(i));
          }
          break;

        case RecipientTypes.Role:
          formData.append(`recipient_names[${recipient.id}]`, recipient.name);
          if (prepare.hasSignerOrder) {
            formData.append(`signer_order[${recipient.id}]`, String(i));
          }
          break;

        case RecipientTypes.Fax:
          break;
        default: unreachable(recipient);
      }

    }

    const response = await hsFetch('/editor/prepare', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (typeof data.url === 'string') {
      return data.url;
    }
    throw new Error('Unhandled response from editor/prepare');
  };

  const getTemplate = async (
    preloadedTsmGroupKey: string,
    templateId: string,
    isBulkSend: boolean,
  ): Promise<TemplateResponse> => {
    const response = await hsFetch(
      `/prep-and-send/use-template/${preloadedTsmGroupKey}${queryParams({
        template_id: templateId,
        is_bulk_send: isBulkSend ? 1 : 0,
      })}`,
    );
    return response.json();
  };

  const phpStatusUploadFile = Yup.object({
    name: Yup.string().required(),
    type: Yup.string().required(),
    root_snapshot_guid: Yup.string().required(),
    pw_required: Yup.bool().default(false),
  });

  // Upload file from user's device
  const uploadFile = async (
    transmissionGroupGuid: string,
    file: File,
  ): Promise<FileUploadResponse | 'TooManyPages' | 'unknown'> => {
    const url = `/attachment/upload/form_id/tsm_group_request/preloaded_tsm_group_key/${transmissionGroupGuid}/load_pdf_info/1`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });

    if (response.status === 200) {
      const rawData = await response.json();

      if (rawData.errors) {
        if (rawData.errors['tsm_group_request[num_snapshots]'].match(/maximum page count/g)) {
          return 'TooManyPages';
        }
        return 'unknown';
      } else {
        const data = await phpStatusUploadFile.validate(rawData);

        return {
          name: data.name,
          rootSnapshotGuid: data.root_snapshot_guid,
          pwRequired: data.pw_required,
        };
      }
    }

    throw new Error('Unhandled response from attachment upload');
  };

  // Delete a user-uploaded file
  const deleteFile = async (
    transmissionGroupGuid: string,
    file: UserFile,
  ): Promise<void> => {
    // if there's an error & a file gets automatically deleted, pass the root snapshot guid
    // since that's the only available snapshot in the database, otherwise pass in the
    // file's guid (user triggering the delete)
    const guid = file.guid || file.rootSnapshotGuid;
    const url = `/attachment/delete${queryParams({
      snapshot_guid: guid,
      preloaded_tsm_group_key: transmissionGroupGuid,
      csrf_token: csrfToken,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    // If the user was attempting to delete a file and it's 404 not found, then
    // it's fine because there's nothing there to delete.
    if (response.status === 200 || response.status === 404) {
      return;
    }

    throw new Error('Unhandled response from attachment delete');
  };

  /**
   * Delete template files for now, naming it "deleteFiles" so we can probably
   * use the endpoint for uploaded file deletion in the future when we
   * switch to using the prep-and-send delete endpoint
   */
  const deleteFiles = async (
    fileGuids: string[],
    preloadedTsmGroupKey: string,
    templateGuid?: string,
    uniqueTokenMap?: {[key: string]: string},
  ): Promise<FilesDeleteResponse> => {
    const url = `/prep-and-send/delete-files/${preloadedTsmGroupKey}${queryParams({
      c: Math.random(),
    })}`;

    const request = {
      files: fileGuids,
      unique_token_map: uniqueTokenMap || null,
      csrf_token: csrfToken,
      template_guid: templateGuid || null,
    };

    const response = await hsFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    if (response.status === 200) {
      return {
        success: data.success,
      };
    } else if (response.status === 400) {
      return {
        success: data.success,
        error: data.error,
      };
    }

    throw new Error('Unhandled response from P&S delete-files');
  };

  const setFilePassword = async (
    rootSnapshotGuid: UserFile['rootSnapshotGuid'],
    password: string,
  ): Promise<FileSetPasswordResponse> => {

    const url = `/attachment/setPassword${queryParams({
      c: Math.random(),
    })}`;

    const formData = new FormData();
    formData.append('password', password);
    formData.append('csrf_token', csrfToken);
    formData.append('root_snapshot_guid', rootSnapshotGuid);

    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });
    if (response.status === 200) {
      return response.json();
    }
    throw new Error('Unhandled response from attachment/setPassword');
  };

  const phpStatusPollFile = {
    [FilePollResponseStatus.Converting]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Converting>(),
      progress: Yup.number(),
      total: Yup.number(),
    }),
    [FilePollResponseStatus.Ok]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Ok>(),
      page_count: Yup.number(),
      guid: Yup.string(),
      tsm_group_guid: Yup.string(),
      has_overlay_data: Yup.bool(),
      is_landscape: Yup.bool(),
    }),
    [FilePollResponseStatus.Deleted]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Deleted>(),
    }),
    [FilePollResponseStatus.Error]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Error>(),
      description: Yup.string(),
    }),
    [FilePollResponseStatus.PasswordRequired]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.PasswordRequired>(),
    }),
    [FilePollResponseStatus.TooManyPages]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.TooManyPages>(),
      max_pages: Yup.number(),
    }),
    // these statuses are applicable for external file downloads (integrations)
    [FilePollResponseStatus.Downloading]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Downloading>(),
    }),
    [FilePollResponseStatus.FileDownloaded]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.FileDownloaded>(),
      is_import: Yup.bool(),
      root_snapshot_guid: Yup.string(),
      root_snapshot_is_accessible: Yup.bool(),
      service_type: Yup.string(),
      name: Yup.string(),
    }),
    [FilePollResponseStatus.FileTooLarge]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.FileTooLarge>(),
    }),
    [FilePollResponseStatus.RetrieveError]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.RetrieveError>(),
    }),
    [FilePollResponseStatus.BadAuth]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.BadAuth>(),
      auth_url: Yup.string(),
    }),
    [FilePollResponseStatus.BadRequest]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.BadRequest>(),
    }),
  };

  // I'd really like to just build `phpStatusPollFile` inline, but I couldn't figure
  // out how to get TypeScript to inver `PollResponse` from `phpStatusPollFile`.
  const pollSchema = lazyUnion<InferUnion<typeof phpStatusPollFile>, 'status'>('status', phpStatusPollFile);
  // Poll API for status of file conversion operation
  const pollFile = async (
    transmissionGroupGuid: string,
    file: UserFile,
    requestType: RequestTypes,
    uniqueToken: string,
    clientId?: string,
    isResend?: boolean,
  ): Promise<FilePollResponse> => {
    const isTemplate = file.type === UserFileTypes.Template ? 1 : 0;
    const url = `/attachment/conversionStatus/is_template/${isTemplate}/form_type_code/${requestType}${queryParams({
      is_from_resend: isResend,
      preloaded_tsm_group_key: transmissionGroupGuid,
      root_snapshot_guid: file.rootSnapshotGuid,
      unique_token: uniqueToken,
      c: Math.random(),
      draft_snapshot_guid: file.draftSnapshotGuid || undefined,
      client_id: clientId || undefined,
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await pollSchema.validate(await response.json());

      switch (data.status) {
        case FilePollResponseStatus.Converting:
          return {
            status: data.status,
            progress: data.progress,
            total: data.total,
          };
        case FilePollResponseStatus.Ok:
          return {
            status: data.status,
            pageCount: data.page_count,
            guid: data.guid,
            tsmGroupGuid: data.tsm_group_guid,
            hasOverlayData: data.has_overlay_data,
            isLandscape: data.is_landscape,
          };
        case FilePollResponseStatus.Deleted:
          return {
            status: data.status,
          };
        case FilePollResponseStatus.PasswordRequired:
          return {
            status: data.status,
          };
        case FilePollResponseStatus.TooManyPages:
          return {
            status: data.status,
            maxPages: data.max_pages,
          };
        case FilePollResponseStatus.Error:
          return {
            status: data.status,
            description: data.description,
          };
        case FilePollResponseStatus.Downloading:
        case FilePollResponseStatus.FileDownloaded:
        case FilePollResponseStatus.FileTooLarge:
        case FilePollResponseStatus.RetrieveError:
        case FilePollResponseStatus.BadAuth:
        case FilePollResponseStatus.BadRequest:
          // these statuses aren't applicable to this endpoint and shouldn't
          // be receiving these for any reason, so breaking to throw error
          break;
        default:
          unreachable(data);
      }
    }
    throw new Error('Unhandled response polling for conversion status');
  };

  const phpStatusPollEmbedded = {
    [FilePollResponseStatus.Ok]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Ok>(),
    }),
    [FilePollResponseStatus.Converting]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Converting>(),
    }),
    [FilePollResponseStatus.Error]: Yup.object({
      status: Yup.mixed<FilePollResponseStatus.Error>(),
      app_info: Yup.object({
        app_name: Yup.string(),
        app_owner_email: Yup.string(),
      }).notRequired(),
    }),
  };

  const pollEmbeddedSchema = lazyUnion<InferUnion<typeof phpStatusPollEmbedded>, 'status'>('status', phpStatusPollEmbedded);

  const pollEmbeddedFiles = async (
    docGuids: string[],
  ): Promise<EmbeddedPollResponse> => {
    // backend needs it as an array
    const guids = docGuids.map((guid) => `doc_guids[]=${guid}`).join('&');
    const url = `/attachment/conversionStatus?${guids}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await pollEmbeddedSchema.validate(await response.json());

      switch (data.status) {
        case FilePollResponseStatus.Ok:
        case FilePollResponseStatus.Converting: {
          return {
            status: data.status,
          };
        }
        case FilePollResponseStatus.Error: {
          let appInfo;
          if (data.app_info) {
            appInfo = {
              appName: data.app_info.app_name,
              appOwnerEmail: data.app_info.app_owner_email,
            };
          }
          return {
            status: data.status,
            appInfo,
          };
        }
        default:
          unreachable(data);
      }
    }

    throw new Error('Unhandled response polling for embedded conversion status');
  };

  // Queue download file from external service
  const externalFileDownload = async (
    serviceType: string,
    fileReference: string,
    fileName: string,
  ): Promise<ExternalFileDownloadResponse> => {
    const url = `/attachment/externalFile${queryParams({
      service_type: serviceType,
      file_reference: fileReference,
      file_name: fileName,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();

      return {
        status: data.status,
        cacheKey: data.cache_key,
      };
    }

    throw new Error('Unhandled response from attachment/externalFile');
  };

  // Poll API for status of external file download operation
  const externalFileProgress = async (
    transmissionGroupGuid: string,
    cacheKey: string,
  ): Promise<FilePollResponse> => {

    const url = `/attachment/externalFileDownloadProgress${queryParams({
      preloaded_tsm_group_key: transmissionGroupGuid,
      cache_key: cacheKey,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await pollSchema.validate(await response.json());

      switch (data.status) {
        case FilePollResponseStatus.FileDownloaded:
          return {
            status: data.status,
            isImport: data.is_import,
            serviceType: data.service_type,
            name: data.name,
            rootSnapshotGuid: data.root_snapshot_guid,
            rootSnapshotIsAccessible: data.root_snapshot_is_accessible,
          };
        case FilePollResponseStatus.Downloading:
        case FilePollResponseStatus.FileTooLarge:
        case FilePollResponseStatus.RetrieveError:
        case FilePollResponseStatus.BadRequest:
          return {
            status: data.status,
          };
        case FilePollResponseStatus.BadAuth:
          return {
            status: data.status,
            authUrl: data.auth_url,
          };
        case FilePollResponseStatus.Converting:
        case FilePollResponseStatus.Ok:
        case FilePollResponseStatus.Deleted:
        case FilePollResponseStatus.PasswordRequired:
        case FilePollResponseStatus.TooManyPages:
          // these statuses aren't applicable to this endpoint and shouldn't
          // be receiving these for any reason, so breaking to throw error
          break;
        case FilePollResponseStatus.Error:
          return {
            status: FilePollResponseStatus.Error,
          };
        default:
          unreachable(data);
      }
    }

    throw new Error('Unhandled response checking external file progress');
  };

  const externalFolderContents = async (
    serviceType: string,
    offset: number,
    folderReference: string,
  ): Promise<ExternalFolderResponse> => {
    const url = `/attachment/externalFolderContents${queryParams({
      service_type: serviceType,
      offset,
      folder_reference: folderReference,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();

      if (data.auth_url) {
        // these are returned for unauthorized integrations
        return {
          authUrl: data.auth_url,
          boxReAuth: data.box_reauth || false,
          googleReAuth: data.google_reauth || false,
        };
      }

      return {
        files: data.files,
        folders: data.folders,
        pageSize: data.page_size,
      };
    }

    throw new Error('Unhandled response getting external folder contents');
  };

  const externalServiceAuthStatus = async (
    serviceType: string,
  ): Promise<ExternalServiceAuthStatusResponse> => {
    const url = `/account/externalServiceAuthStatus${queryParams({
      service_type: serviceType,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();

      return {
        authorized: data.authorized,
        failed: data.failed,
        errorMessage: data.error_msg,
      };
    }

    throw new Error('Unhandled response from externalServiceAuthStatus');
  };

  // Reorder uploaded files
  const reorderFiles = async (
    transmissionGroupGuid: string,
    files: UserFilesKeyed,
  ): Promise<FileReorderResponse> => {
    const url = `/attachment/asyncReorderSnapshots${queryParams({
      c: Math.random(),
    })}`;

    const formData = new FormData();
    formData.append('preloaded_tsm_group_key', transmissionGroupGuid);
    formData.append('csrf_token', csrfToken);
    Object.values(files).filter(notEmpty).forEach((file) => {
      formData.append(`snapshot_order[${file.guid}]`, String(file.order));
    });

    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });

    if (response.status === 200) {
      const data = await response.json();

      return data;
    }

    throw new Error('Unhandled response reordering files');
  };
  // update account, BE determines the site and
  // updates received for FAX and requested for SR
  const updateAccount = async (
    firstName: string,
    lastName: string,
    shouldIncludePdfs: boolean,
    shouldIncludePdfsForOthers: boolean,
    isHelloFax: boolean,
  ): Promise<UpdateAccountResponse> => {
    const url = `/account/update${queryParams({
      c: Math.random(),
      ajax: true,
    })}`;

    const formData = new FormData();
    formData.append('edit_account[first_name]', firstName);
    formData.append('edit_account[last_name]', lastName);
    if (isHelloFax) {
      formData.append('edit_account[should_include_received_fax_pdfs]', String(shouldIncludePdfs));
      formData.append('edit_account[should_include_received_fax_pdfs_for_others]', String(shouldIncludePdfsForOthers));
      // should we change this to snake case to match othes? if so BE needs the change tooo.
      formData.append('mode', 'update-fax-pdf-attachments');
    } else {
      formData.append('edit_account[should_include_requested_pdfs]', String(shouldIncludePdfs));
      formData.append('edit_account[should_include_requested_pdfs_for_others]', String(shouldIncludePdfsForOthers));
      formData.append('mode', 'update_name');
    }
    formData.append('edit_account[_csrf_token]', csrfToken);

    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });

    return response.json();
  };

  const templateCreateStatusResponse = {
    error: Yup.object().shape({
      status: Yup.mixed<'error'>(),
      description: Yup.string().notRequired().default(undefined),
    }),
    pending: Yup.object().shape({
      status: Yup.mixed<'pending'>(),
    }),
    ok: Yup.object().shape({
      status: Yup.mixed<'ok'>(),
      template_guid: Yup.string().required(),
      is_reusable_link: Yup.boolean().required(),
      reusable_link_was_edited: Yup.boolean().required(),
      manage_page_url: Yup.string().required(),
    }),
  };
  const templateCreateStatusResponseSchema = lazyUnion<
    InferUnion<typeof templateCreateStatusResponse>, 'status'
  >('status', templateCreateStatusResponse);

  const getCreateTemplateStatus = async (guid: string): Promise<TemplateCreateStatus> => {
    const url = `/send/createTemplateStatus${queryParams({
      guid,
      c: Math.random(),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await templateCreateStatusResponseSchema.validate(await response.json());

      switch (data.status) {
        case 'pending':
          return {
            status: data.status,
          };
        case 'error':
          return {
            status: data.status,
            description: data.description,
          };
        case 'ok':
          return {
            status: data.status,
            templateGuid: data.template_guid,
            isReusableLink: data.is_reusable_link,
            reusableLinkWasEdited: data.reusable_link_was_edited,
            managePageUrl: data.manage_page_url,
          };
        default:
          unreachable(data);
      }
    }


    throw new Error('Unhandled response from createTemplateStatus');
  };

  const contactSchema = Yup.object({
    id: Yup.number(),
    value: Yup.string(),
    guid: Yup.string(),
    numUses: Yup.number(),
    // Email or Fax
    typeCode: Yup.mixed().oneOf(['E', 'F']),
    contact: Yup.object({
      id: Yup.number(),
      // Automatically convert `null` names to an empty string
      name: Yup.string().default(''),
    }),
  }).camelCase();

  const getReferAFriendURL = async (): Promise<string> => {
    const response = await hsFetch('/account/getReferAFriendURL');
    const data = await response.json();

    return data.url;
  };
  const getContactImportStatus = async (type: 'gmail' | 'yahoo'): Promise<ImportContactsStatusResponse> => {
    const url = appendQueryParameters('/account/getContactImportStatus', {
      type,
      ignore_timeout: 1,
      c: Math.random(),
    });

    const response = await hsFetch(url);
    const data = await response.json();

    return {
      success: data.success,
      complete: data.complete,
      progress: data.progress,
      count: data.count,
    };
  };


  type InviteResponse = {
    success: boolean,
    invitesSent: number
  }
  const inviteFriends = async (emails: string[]): Promise<InviteResponse> => {

    const formData = new FormData();
    formData.append('sendinviteemails[_csrf_token]', getCSRFToken());
    emails.forEach((email) => formData.append('emails[]', email));

    const response = await hsFetch('/account/asyncSendInviteEmails', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();

    return {
      success: data.success,
      invitesSent: data.invites_sent,
    };
  };
  const getAllContacts = async (): Promise<Contact[]> => {
    const response = await hsFetch(`/account/getAllContacts?c=${Math.random()}`, {
      headers: {
        // If you don't include this you either get a 404 or a 500
        'x-requested-with': 'XMLHttpRequest',
      },
    });
    const data: Record<Contact['value'], Contact> = await response.json();
    const serverContacts = await Yup.array().of(contactSchema).validate(Object.values(data));

    return serverContacts.map((c): Contact => {
      return {
        id: c.guid,
        name: c.contact.name,
        value: c.value,
        type: c.typeCode,
        numUses: c.numUses,
      };
    });

  };

  const bulkSendExampleResponseSchema = Yup.object<ExampleFields>({
    fields: Yup.array().of(Yup.string()).required(),
  });

  const bulkSendValidateErrorResponseSchema = Yup.object().shape({
    error: Yup.string().required(),
  });

  type BulkSendValidateErrorResponse = Yup.InferType<typeof bulkSendValidateErrorResponseSchema>;

  const bulkSendInitResponseSchema = Yup.object<BulkSendInitResponse>({
    signerKey: Yup.string().required(),
    isCcEnabled: Yup.boolean(),
    form: Yup.object({
      lockedTitle: Yup.string().nullable(),
      lockedMessage: Yup.string().nullable(),
    }).camelCase(),
  }).camelCase();

  const initBulkSend = async (cacheKey: string): Promise<BulkSendInitResponse> => {
    const url = `/bulksend/init?cache_key=${cacheKey}`;

    const response = await hsFetch(url, {
      credentials: 'same-origin',
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
    if (response.status === 200) {
      const data = await response.json();
      return bulkSendInitResponseSchema.validate(data);
    }

    throw new Error(response.statusText);
  };

  const getBulkSendExample = async (templates: TemplateResponse[]): Promise<ExampleFields> => {

    const url = `/bulksend/example${queryParams({
      'template_ids[]': templates.map((t) => t.templateGuid),
    })}`;

    const response = await hsFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return bulkSendExampleResponseSchema.validate(data);
    }
    throw new Error(response.statusText);
  };

  const validateBulkSendData = async (
    file: File,
    signerKey: string,
    templateIds: string[],
  ): Promise<null|BulkSendValidateErrorResponse> => {

    const url = `/bulksend/signers${queryParams({
      signer_key: signerKey,
      'template_ids[]': templateIds,
    })}`;

    const formData = new FormData();
    formData.append('signer_file', file);
    formData.append('csrf_token', csrfToken);

    const response = await hsFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });
    if (response.status !== 200) {
      return bulkSendValidateErrorResponseSchema.validate(await response.json());
    }
    return null;
  };

  const bulkSendSubmitResponseSchema = Yup.object().shape({
    bulk_send_job_id: Yup.string().required(),
    total: Yup.number().required(),
  });

  const submitBulkSendRequest = async (
    signerKey: string,
    title: string,
    message: string,
  ): Promise<string> => {
    const url = '/bulksend';
    const formData = new FormData();
    formData.append('signer_key', signerKey);
    formData.append('title', title);
    formData.append('message', message);
    formData.append('csrf_token', csrfToken);
    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });

    if (response.status === 200) {
      await bulkSendSubmitResponseSchema.validate(await response.json());
      return '/home/manage?filter=bulk';
    }

    throw new Error(response.statusText);
  };

  return {
    getPrepAndSendData,
    savePrepAndSendData,
    sendPrepAndSendRequest,
    selfSavePrepAndSendRequest,
    prepareEditor,
    uploadFile,
    deleteFile,
    deleteFiles,
    getTemplate,
    pollFile,
    pollEmbeddedFiles,
    externalFileDownload,
    externalFileProgress,
    externalFolderContents,
    externalServiceAuthStatus,
    reorderFiles,
    updateAccount,
    inviteFriends,
    getAllContacts,
    getReferAFriendURL,
    getContactImportStatus,
    setFilePassword,
    getCreateTemplateStatus,
    getBulkSendExample,
    initBulkSend,
    validateBulkSendData,
    submitBulkSendRequest,
  };
}
